import {PointerEvent, useCallback, useMemo, useRef, useState} from 'react';

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;

type Point = {x: number; y: number};

// Zoom (slider) + pan (drag) state for the seat grid, with a reset back to the fitted overview.
// `anchor` is the ticket's own seat as an offset from the grid center, in on-screen px before zoom.
// At MIN_ZOOM the pan stays at 0 so the whole block is framed; the closer the zoom gets to
// MAX_ZOOM the more the seat is pulled to the middle of the viewport, so it never zooms out of frame.
export default function useSeatZoom(anchor?: Point) {
    const [zoom, setZoomState] = useState(MIN_ZOOM);
    const [drag, setDrag] = useState<Point>({x: 0, y: 0});
    const dragRef = useRef(drag);
    dragRef.current = drag;
    const gesture = useRef<{startX: number; startY: number; drag: Point} | null>(null);

    const setZoom = useCallback((value: number) => {
        setZoomState(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)));
    }, []);

    const reset = useCallback(() => {
        setZoomState(MIN_ZOOM);
        setDrag({x: 0, y: 0});
    }, []);

    const pan = useMemo(() => {
        if(!anchor) {
            return drag;
        }
        const centering = (zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM);
        return {
            x: drag.x - anchor.x * zoom * centering,
            y: drag.y - anchor.y * zoom * centering
        };
    }, [anchor, zoom, drag]);

    const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
        gesture.current = {startX: event.clientX, startY: event.clientY, drag: dragRef.current};
        event.currentTarget.setPointerCapture(event.pointerId);
    }, []);

    const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if(!gesture.current) {
            return;
        }
        setDrag({
            x: gesture.current.drag.x + (event.clientX - gesture.current.startX),
            y: gesture.current.drag.y + (event.clientY - gesture.current.startY)
        });
    }, []);

    const endDrag = useCallback(() => {
        gesture.current = null;
    }, []);

    return {zoom, setZoom, pan, reset, dragHandlers: {onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag}};
}
